/* src/pages/ManageDonation/index.jsx */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./index.scss";
import { useSelector } from "react-redux";
import { getSchedule, cancelSchedule, pauseSchedule, resumeSchedule } from "../../api/payments";
import { getCustomerInfo } from "../../api/donor";
import ProtectedRoute from "../../components/ProtectedRoute";
import { useAuth } from "../../contexts/AuthContext";
import moment from "moment-timezone";

// 1) Import i18n
import { useTranslation } from "react-i18next";

const ManageDonationContent = () => {
  // 2) Destructure t
  const { t } = useTranslation();
  const { signOut, user } = useAuth();
  const navigate = useNavigate();

  const Funds = useSelector((state) => state.donation.funds);
  const { scheduleId } = useParams();

  // Track if user is admin
  const [isAdmin, setIsAdmin] = React.useState(false);

  // Check if user has admin claims
  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          setIsAdmin(!!tokenResult.claims.admin);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseDate, setPauseDate] = useState("");
  const [error, setError] = useState(null);

  // Log authentication state for debugging
  useEffect(() => {
    if (user) {
      console.log('User authenticated:', {
        email: user.email,
        uid: user.uid,
        emailVerified: user.emailVerified
      });
    } else {
      console.log('No user authenticated');
    }
  }, [user]);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const data = await getSchedule(scheduleId);
        const customerInfo = await getCustomerInfo(data.customerId);
        data.donorDetails = customerInfo || data.donorDetails;
        setSchedule(data);
      } catch (err) {
        console.error('Error fetching schedule:', err);
        // Check for Firebase permission error
        const errorMessage = err.response?.data?.message || err.message || '';
        if (errorMessage.includes('Missing or insufficient permissions') || 
            errorMessage.includes('permission-denied') ||
            err.code === 'permission-denied') {
          setError('PERMISSION_DENIED');
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [scheduleId]);

  const handleCancel = async () => {
    setCanceling(true);
    setError(null);
    try {
      const updated = await cancelSchedule(scheduleId);
      setSchedule({ ...schedule, ...updated });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setCanceling(false);
    }
  };

  const handlePause = async () => {
    if (!pauseDate) {
      setError("Please select a pause date");
      return;
    }

    // Validate pause date (max 6 months)
    const maxPauseDate = moment().add(6, 'months').toDate();
    const selectedDate = new Date(pauseDate);
    
    if (selectedDate > maxPauseDate) {
      setError("Pause period cannot exceed 6 months");
      return;
    }

    if (selectedDate <= new Date()) {
      setError("Pause date must be in the future");
      return;
    }

    setPausing(true);
    setError(null);
    try {
      const updated = await pauseSchedule(scheduleId, selectedDate);
      setSchedule({ ...schedule, ...updated });
      setShowPauseModal(false);
      setPauseDate("");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    setError(null);
    try {
      const updated = await resumeSchedule(scheduleId);
      setSchedule({ ...schedule, ...updated });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setResuming(false);
    }
  };

  const openPauseModal = () => {
    setShowPauseModal(true);
    setError(null);
    // Set default pause date to 1 month from now
    const defaultDate = moment().add(1, 'month').format('YYYY-MM-DD');
    setPauseDate(defaultDate);
  };

  const closePauseModal = () => {
    setShowPauseModal(false);
    setPauseDate("");
    setError(null);
  };

  const handleSignOut = async () => {
    try {
      const result = await signOut();
      if (result.success) {
        // Clear any stored data
        window.localStorage.removeItem('emailForSignIn');
        window.localStorage.removeItem('donorInfo');
        // Redirect to home page after sign out
        navigate('/');
      }
    } catch (err) {
      console.error('Error signing out:', err);
      // Force redirect even if there's an error
      navigate('/');
    }
  };

  const handleBack = () => {
    // Navigate based on admin status
    if (isAdmin) {
      navigate('/admin');
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="manage-donation-container">
        <p>{t("manageDonationLoading")}</p>
      </div>
    );
  }

  if (error) {
    // Check if it's a permission error
    if (error === 'PERMISSION_DENIED') {
      return (
        <div className="manage-donation-container">
          <div className="error-page">
            <div className="error-icon">🔒</div>
            <h2>{t("manageDonationPermissionDeniedTitle") || "Access Denied"}</h2>
            <p className="error-message">
              {t("manageDonationPermissionDeniedMessage") || "You don't have permission to view this donation schedule."}
            </p>
            <p className="error-description">
              {t("manageDonationPermissionDeniedDescription") || "This may happen if you're trying to access a donation that doesn't belong to you or if your session has expired."}
            </p>
            <button 
              onClick={handleSignOut}
              className="sign-out-button-error"
            >
              {t("manageDonationSignOut") || "Sign Out"}
            </button>
          </div>
        </div>
      );
    }
    
    // For other errors
    return (
      <div className="manage-donation-container">
        <div className="error-page">
          <div className="error-icon">⚠️</div>
          <h2>{t("manageDonationErrorTitle") || "Something Went Wrong"}</h2>
          <p className="error-message">
            {t("manageDonationError")}: {error}
          </p>
          <p className="error-description">
            {t("manageDonationErrorDescription") || "If you logged in with the wrong account, please sign out and try again."}
          </p>
          <button 
            onClick={handleSignOut}
            className="sign-out-button-error"
          >
            {t("manageDonationSignOut") || "Sign Out"}
          </button>
        </div>
      </div>
    );
  }

  // If no schedule data found or error, handle gracefully
  if (!schedule) {
    return (
      <div className="manage-donation-container">
        <h2>{t("manageDonationTitle")}</h2>
        <p className="error">
          {t("manageDonationError")}: Missing schedule data
        </p>
      </div>
    );
  }

  const {
    donorDetails,
    template,
    donationScheduleBreakdown = [],
    status,
    pausedUntil
  } = schedule;

  // Helper function to convert Firestore Timestamp to Date
  const convertFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return null;
    // Check if it's a Firestore Timestamp object with seconds property
    if (timestamp.seconds !== undefined) {
      return new Date(timestamp.seconds * 1000);
    }
    // Otherwise, try to parse it as a regular date
    return new Date(timestamp);
  };

  // Convert pausedUntil to a proper date
  const pausedUntilDate = convertFirestoreTimestamp(pausedUntil);

  // Check if currently paused
  const isPaused = pausedUntilDate && moment(pausedUntilDate).isAfter(moment());

  // Calculate recurring amount (from the first donation in the schedule)
  const recurringAmount = donationScheduleBreakdown.length > 0
    ? donationScheduleBreakdown[0].allocations.reduce(
        (sum, alloc) => sum + Number(alloc.amount),
        0
      )
    : 0;

  // Collect cause names
  let causeNamesSet = new Set();
  if (donationScheduleBreakdown.length > 0) {
    const firstDonation = donationScheduleBreakdown[0];
    causeNamesSet = new Set(
      firstDonation.allocations.map((alloc) => {
        const foundFund = Funds
          ? Funds.find((f) => f.fundId === alloc.fundId)
          : null;
        const causeName = foundFund
          ? foundFund.name
          : t("manageDonationUnknownCause");
        return causeName;
      })
    );
  }


  return (
    <div className="manage-donation-container">
      <div className="donation-header">
        <button 
          onClick={handleBack}
          className="back-button"
          title={t("manageDonationBack") || "Go Back"}
        >
          ← {t("manageDonationBack") || "Back"}
        </button>
        <h2>{t("manageDonationTitle")}</h2>
        <button 
          onClick={handleSignOut}
          className="sign-out-button"
          title={t("manageDonationSignOut") || "Sign Out"}
        >
          {t("manageDonationSignOut") || "Sign Out"}
        </button>
      </div>
      <div className="top-row">
        <div className="schedule-info-card">
          <div className="info-row">
            <div className="info-label">{t("manageDonationDonorLabel")}:</div>
            <div className="info-value">
              {donorDetails?.firstName} {donorDetails?.lastName}
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">{t("manageDonationEmailLabel")}:</div>
            <div className="info-value">{donorDetails?.email}</div>
          </div>
          <div className="info-row">
            <div className="info-label">{t("manageDonationStatusLabel")}:</div>
            <div className="info-value">
              <span className={`status-pill ${status}`}>{status}</span>
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">
              {t("manageDonationTemplateLabel")}:
            </div>
            <div className="info-value">{template || "N/A"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">{t("manageDonationRecurringAmountLabel") || "Recurring Amount"}:</div>
            <div className="info-value">£{recurringAmount.toFixed(2)}</div>
          </div>
          {isPaused && (
            <div className="info-row">
              <div className="info-label">{t("manageDonationPausedUntilLabel") || "Paused Until"}:</div>
              <div className="info-value">
                <span className="status-pill paused">
                  {moment(pausedUntilDate).format('MMMM D, YYYY')}
                </span>
              </div>
            </div>
          )}
          <div className="info-row">
            <div className="info-label">{t("manageDonationCausesLabel")}:</div>
            <div className="info-value">
              {causeNamesSet.size === 0
                ? "N/A"
                : Array.from(causeNamesSet).map((causeName) => {
                    const foundFund = Funds
                      ? Funds.find((f) => f.name === causeName)
                      : null;
                    const icon = foundFund && foundFund.iconSVGCode && (
                      <span
                        className="cause-icon"
                        dangerouslySetInnerHTML={{
                          __html: foundFund.iconSVGCode
                        }}
                      />
                    );

                    return (
                      <div className="cause-item" key={causeName}>
                        {icon && (
                          <span className="cause-icon-wrapper">{icon}</span>
                        )}
                        <span className="cause-name">{causeName}</span>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      </div>

      {(status === "pending" || status === "paused") && (
        <div className="action-buttons">
          {isPaused ? (
            <button 
              className="resume-donation-button"
              onClick={handleResume}
              disabled={resuming}
            >
              {resuming ? t("manageDonationResuming") || "Resuming..." : t("manageDonationResumeDonation") || "Resume Donation"}
            </button>
          ) : (
            <button 
              className="pause-donation-button"
              onClick={openPauseModal}
              disabled={pausing}
            >
              {t("manageDonationPauseDonation") || "Pause Donation"}
            </button>
          )}
          <button 
            className="cancel-donation-button"
            onClick={handleCancel}
            disabled={canceling}
          >
            {canceling ? t("manageDonationCancelling") || "Cancelling..." : t("manageDonationCancelDonation") || "Cancel Donation"}
          </button>
        </div>
      )}

      {status === "cancelled" && (
        <p className="info-message">{t("manageDonationDonationCancelled")}</p>
      )}

      {status === "completed" && (
        <p className="info-message">{t("manageDonationDonationCompleted")}</p>
      )}

      {showPauseModal && (
        <div className="modal-overlay" onClick={closePauseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t("manageDonationPauseModalTitle") || "Pause Donation"}</h3>
            <p>{t("manageDonationPauseModalDescription") || "Select a date to pause your donation until (maximum 6 months):"}</p>
            <input
              type="date"
              value={pauseDate}
              onChange={(e) => setPauseDate(e.target.value)}
              min={moment().add(1, 'day').format('YYYY-MM-DD')}
              max={moment().add(6, 'months').format('YYYY-MM-DD')}
              className="pause-date-input"
            />
            {error && <p className="error-message">{error}</p>}
            <div className="modal-buttons">
              <button 
                className="modal-confirm-button"
                onClick={handlePause}
                disabled={pausing || !pauseDate}
              >
                {pausing ? t("manageDonationPausing") || "Pausing..." : t("manageDonationConfirmPause") || "Confirm Pause"}
              </button>
              <button 
                className="modal-cancel-button"
                onClick={closePauseModal}
                disabled={pausing}
              >
                {t("manageDonationCancelButton") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ManageDonation = () => {
  const { t } = useTranslation();
  
  return (
    <ProtectedRoute 
      title={t("manageDonationAuthTitle") || "Donation Management Access"}
    >
      <ManageDonationContent />
    </ProtectedRoute>
  );
};

export default ManageDonation;
